/**
 * Onboarding wizard (onboarding.md §1.2, §4, §5).
 *
 * Two steps, rendered in place of the normal app routes whenever the tenant
 * has `industry_template === null` (wired in App.tsx):
 *
 *   Step 1 — Industry template: three stacked cards (§4.2). Required pick.
 *   Step 2 — Account setup: three paths (§5.1) — CSV upload / add one
 *            manually / "I'll add accounts later".
 *
 * On completion the wizard calls `onComplete()`, which re-fetches /api/me in
 * App.tsx so the tenant's now-set `industry_template` flips the app into
 * normal rendering and the user lands on /today.
 *
 * Scope (B.2): industry pick seeds icp_filter + scanner_config server-side
 * (no coverage taxonomy yet); CSV import parses in-memory (no R2); imported /
 * added accounts get no AI enrichment. All three are server-side concerns —
 * this screen just drives the UX.
 */
import { useRef, useState } from 'preact/hooks';
import {
  type IndustryTemplateId,
  addAccountOnboarding,
  importCsv,
  setIndustryTemplate,
  skipImport,
} from '../api.ts';

interface OnboardingProps {
  /** Called once the wizard is done — App.tsx re-fetches /api/me. */
  onComplete: () => void;
  /** The tenant's first name (or workspace name) for the greeting, if known. */
  workspaceName?: string | null;
}

type Step = 'industry' | 'accounts';

interface TemplateCard {
  id: IndustryTemplateId;
  name: string;
  description: string;
  seeds: string[];
}

// Card copy per onboarding.md §4.2 — name, one-line description, and a short
// bullet list of what the template seeds.
const TEMPLATE_CARDS: TemplateCard[] = [
  {
    id: 'it_solutions',
    name: 'IT Solutions',
    description: 'For VARs, MSPs, and IT solutions sellers.',
    seeds: [
      'Buyer personas: CxO, Manager/Director, Engineer/Admin',
      'News + company-feed scanners tuned for IT accounts',
      'VAR/MSP briefing voice — portfolio, coverage, deal cycle',
    ],
  },
  {
    id: 'wealth_advisor',
    name: 'Wealth Advisor',
    description: 'For wealth-management and advisory practices.',
    seeds: [
      'Buyer personas: Principal, Advisor, Operations Manager',
      'News + company-feed scanners for advisory firms',
      'Wealth-management briefing voice — households, AUM',
    ],
  },
  {
    id: 'custom',
    name: 'Custom',
    description: "For everything else — you'll shape it as you go.",
    seeds: [
      'One generic "Buyer" persona you refine in Settings',
      'News + company-feed scanners with default tuning',
      'Generic professional briefing voice',
    ],
  },
];

export function Onboarding({ onComplete, workspaceName }: OnboardingProps) {
  const [step, setStep] = useState<Step>('industry');

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-canvas)',
        color: 'var(--text)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 24px',
      }}
    >
      <div style={{ maxWidth: 560, width: '100%' }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-faint)',
              marginBottom: 6,
            }}
          >
            Setup · Step {step === 'industry' ? '1' : '2'} of 2
          </div>
          <h1
            style={{
              fontFamily: 'var(--f-serif)',
              fontStyle: 'italic',
              fontSize: 32,
              fontWeight: 400,
              margin: 0,
            }}
          >
            {step === 'industry'
              ? `Welcome${workspaceName ? `, ${workspaceName}` : ''}.`
              : 'Add your accounts'}
          </h1>
        </div>

        {step === 'industry' ? (
          <IndustryStep onPicked={() => setStep('accounts')} />
        ) : (
          <AccountsStep onComplete={onComplete} />
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 1 — Industry template (§4)
// ───────────────────────────────────────────────────────────────────────────

function IndustryStep({ onPicked }: { onPicked: () => void }) {
  const [selected, setSelected] = useState<IndustryTemplateId | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleContinue() {
    if (!selected || saving) return;
    setSaving(true);
    setError(null);
    try {
      await setIndustryTemplate(selected);
      onPicked();
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <div>
      <p class="muted" style={{ marginBottom: 20, fontSize: 14 }}>
        Pick the industry that fits how you sell. This seeds your buyer personas and account
        scanners — you can change all of it later in Settings.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {TEMPLATE_CARDS.map((card) => {
          const isSelected = selected === card.id;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setSelected(card.id)}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                padding: '16px 18px',
                borderRadius: 'var(--r-lg)',
                background: isSelected ? 'var(--accent-tint-14)' : 'var(--bg-panel)',
                border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                transition: 'background 120ms, border-color 120ms',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-sans)',
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {card.name}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                {card.description}
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 12.5,
                  color: 'var(--text-2)',
                  lineHeight: 1.6,
                }}
              >
                {card.seeds.map((seed) => (
                  <li key={seed}>{seed}</li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {error && (
        <p class="error-banner" style={{ marginBottom: 12 }}>
          {error}
        </p>
      )}

      <button
        type="button"
        class="btn btn-primary"
        disabled={!selected || saving}
        onClick={handleContinue}
      >
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 2 — Account setup (§5)
// ───────────────────────────────────────────────────────────────────────────

type AccountsPath = 'choose' | 'csv' | 'manual';

function AccountsStep({ onComplete }: { onComplete: () => void }) {
  const [path, setPath] = useState<AccountsPath>('choose');
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSkip() {
    if (finishing) return;
    setFinishing(true);
    setError(null);
    try {
      await skipImport();
      onComplete();
    } catch (err) {
      setError((err as Error).message);
      setFinishing(false);
    }
  }

  if (path === 'csv') {
    return <CsvImportPath onDone={onComplete} onBack={() => setPath('choose')} />;
  }
  if (path === 'manual') {
    return <ManualAddPath onDone={onComplete} onBack={() => setPath('choose')} />;
  }

  return (
    <div>
      <p class="muted" style={{ marginBottom: 20, fontSize: 14 }}>
        Bring in the accounts you're tracking. You can import a CSV, add one by hand, or skip this
        and add accounts whenever you're ready.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        <PathCard
          title="Upload a CSV"
          body="Import up to 200 accounts at once. We'll auto-detect your columns."
          onClick={() => setPath('csv')}
        />
        <PathCard
          title="Add one manually"
          body="Type in a single account to get started — add more anytime."
          onClick={() => setPath('manual')}
        />
        <PathCard
          title="I'll add accounts later"
          body="Skip for now. Your portfolio starts empty and you can add accounts from /today."
          onClick={handleSkip}
          disabled={finishing}
        />
      </div>

      {error && <p class="error-banner">{error}</p>}
    </div>
  );
}

function PathCard({
  title,
  body,
  onClick,
  disabled,
}: {
  title: string;
  body: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        padding: '16px 18px',
        borderRadius: 'var(--r-lg)',
        background: 'var(--bg-panel)',
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{body}</div>
    </button>
  );
}

// ── CSV import path ────────────────────────────────────────────────────────

function CsvImportPath({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  async function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setResult(null);
    setImporting(true);
    try {
      const text = await file.text();
      const res = await importCsv(text);
      setResult({ imported: res.imported, skipped: res.skipped_duplicates });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
      // Allow re-selecting the same file after a failed import.
      input.value = '';
    }
  }

  return (
    <div>
      <p class="muted" style={{ marginBottom: 16, fontSize: 14 }}>
        Upload a CSV with your accounts. We look for a name column (also "Account Name" or
        "Company") plus optional domain, industry, and location columns. Max 200 accounts, 1 MB.
      </p>

      {!result && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            class="btn btn-primary"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
          >
            {importing ? 'Importing…' : 'Choose a CSV file'}
          </button>
          {fileName && !importing && (
            <p class="muted" style={{ marginTop: 8, fontSize: 12 }}>
              Selected: {fileName}
            </p>
          )}
        </>
      )}

      {error && (
        <p class="error-banner" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}

      {result && (
        <div
          style={{
            marginTop: 4,
            padding: '14px 16px',
            background: 'var(--accent-tint-08)',
            border: '1px solid var(--accent-tint-22)',
            borderRadius: 'var(--r-lg)',
            fontSize: 14,
          }}
        >
          Imported <strong>{result.imported}</strong>{' '}
          {result.imported === 1 ? 'account' : 'accounts'}.
          {result.skipped > 0 && <> Skipped {result.skipped} that already existed.</>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        {result ? (
          <button type="button" class="btn btn-primary" onClick={onDone}>
            Continue to today
          </button>
        ) : (
          <button type="button" class="btn" disabled={importing} onClick={onBack}>
            Back
          </button>
        )}
      </div>
    </div>
  );
}

// ── Manual single-account path (§5.6) ──────────────────────────────────────

function ManualAddPath({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string[]>([]);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const entity = await addAccountOnboarding({
        name: name.trim(),
        domain: domain.trim() || undefined,
      });
      setAdded((prev) => [...prev, entity.name]);
      setName('');
      setDomain('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <p class="muted" style={{ marginBottom: 16, fontSize: 14 }}>
        Add an account by name. A domain is optional but helps you recognize it later.
      </p>

      {added.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            background: 'var(--accent-tint-08)',
            border: '1px solid var(--accent-tint-22)',
            borderRadius: 'var(--r-lg)',
            fontSize: 13,
          }}
        >
          Added: {added.join(', ')}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <label
          for="onb-acct-name"
          style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}
        >
          Account name
        </label>
        <input
          id="onb-acct-name"
          type="text"
          class="search-bar"
          placeholder="Acme Corp"
          value={name}
          // biome-ignore lint/a11y/noAutofocus: §6.2 calls for name autofocus on the add-account form
          autoFocus
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          style={{ marginBottom: 12, width: '100%' }}
        />
        <label
          for="onb-acct-domain"
          style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}
        >
          Domain{' '}
          <span class="muted" style={{ fontWeight: 400 }}>
            (optional)
          </span>
        </label>
        <input
          id="onb-acct-domain"
          type="text"
          class="search-bar"
          placeholder="e.g., acmecorp.com"
          value={domain}
          onInput={(e) => setDomain((e.target as HTMLInputElement).value)}
          style={{ marginBottom: 12, width: '100%' }}
        />

        {error && (
          <p class="error-banner" style={{ marginBottom: 12 }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={saving || name.trim().length === 0}
          >
            {saving ? 'Adding…' : 'Add account'}
          </button>
          {added.length > 0 ? (
            <button type="button" class="btn" onClick={onDone}>
              Continue to today
            </button>
          ) : (
            <button type="button" class="btn" disabled={saving} onClick={onBack}>
              Back
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
